import type {
  Column as ReactTableColumn,
  RowData,
  Table as ReactTable,
} from '@tanstack/react-table';
import {Plus, X} from 'lucide-react';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {toggleVariants} from '@/components/ui/toggle';
import {cn} from '@/utils/ui';

import {SelectFilterMenuItem} from './filter-types/SelectFilter';
import {TextFilterMenuItem} from './filter-types/TextFilter';
import type {TableFilterMeta} from './tableFilterTypes';
import {getTextFilterValue} from './tableFilterTypes';

type TableFilterProps<TData extends RowData> = {
  table: ReactTable<TData>;
};

function TableFilter<TData extends RowData>({table}: TableFilterProps<TData>) {
  const {t} = useTranslation();

  const availableFilters = table
    .getAllLeafColumns()
    .map(column => getAvailableFilter(column, table))
    .filter((filter): filter is AvailableFilter<TData> => filter !== null);

  const activeFilters = availableFilters
    .map((filter): ActiveFilter<TData> | null => {
      const value = filter.column?.getFilterValue();

      if (value === undefined) return null;

      if (filter.filter.type === 'text') {
        const textValue = getTextFilterValue(value);

        if (textValue === null) return null;

        return {...filter, displayValue: textValue};
      }

      const options =
        typeof filter.filter.options === 'function'
          ? filter.filter.options(table)
          : filter.filter.options;
      const selectedOption = options.find(option => option.value === value);

      if (!selectedOption) {
        return null;
      }

      return {...filter, displayValue: selectedOption.label};
    })
    .filter((filter): filter is ActiveFilter<TData> => filter !== null);

  const handleRemoveFilter = (filter: ActiveFilter<TData>) => {
    filter.column?.setFilterValue(undefined);
  };

  if (!availableFilters.length && !activeFilters.length) {
    return null;
  }

  return (
    <div className="flex flex-row flex-wrap items-center gap-1">
      {!!availableFilters.length && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="secondary" size="small">
              <Plus size={14} />
              {t('Add Filter')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {availableFilters.map(filter => (
              <FilterMenuItem
                filter={filter}
                key={filter.column.id}
                table={table}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {activeFilters.map(filter => (
        <div
          key={filter.column.id}
          className="inline-flex h-11 items-center justify-center"
        >
          <div
            className={cn(
              toggleVariants({variant: 'tag', size: 'small'}),
              'gap-2'
            )}
          >
            <span>
              {filter.label}: {filter.displayValue}
            </span>
            <Button
              type="button"
              variant="warning"
              size="text"
              className="rounded-full p-0.5"
              onClick={() => handleRemoveFilter(filter)}
            >
              <span className="sr-only">
                {t('Delete')} {filter.label}
              </span>
              <X size={14} />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

type AvailableFilter<TData extends RowData> = {
  column: ReactTableColumn<TData, unknown>;
  filter: TableFilterMeta<TData>;
  label: string;
};

type ActiveFilter<TData extends RowData> = AvailableFilter<TData> & {
  displayValue: string;
};

function getAvailableFilter<TData extends RowData>(
  column: ReactTableColumn<TData, unknown>,
  table: ReactTable<TData>
): AvailableFilter<TData> | null {
  const metaFilter = column.columnDef.meta?.filter;
  const header = column.columnDef.header?.toString() || column.id;

  if (!metaFilter || !column.getCanFilter()) return null;

  if (metaFilter.type === 'select') {
    const options =
      typeof metaFilter.options === 'function'
        ? metaFilter.options(table)
        : metaFilter.options;

    if (!options.length) return null;
  }

  return {
    column,
    filter: metaFilter,
    label: metaFilter.label ?? header,
  };
}

type FilterMenuItemProps<TData extends RowData> = {
  filter: AvailableFilter<TData>;
  table: ReactTable<TData>;
};

function FilterMenuItem<TData extends RowData>({
  filter,
  table,
}: FilterMenuItemProps<TData>) {
  switch (filter.filter.type) {
    case 'text':
      return (
        <TextFilterMenuItem
          column={filter.column}
          filter={filter.filter}
          label={filter.label}
        />
      );
    case 'select':
      return (
        <SelectFilterMenuItem
          column={filter.column}
          filter={filter.filter}
          label={filter.label}
          table={table}
        />
      );
  }
}

export default TableFilter;
